// Account — user profile settings.
// Clean two-column layout: profile card on left, settings on right.
// Keeps all the original fetch/update logic but with a much cleaner presentation.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';
import PageLayout from './components/PageLayout';
import { useSubscription } from './services/useSubscription';
import { colors } from './theme';
import { isRaphaDomain } from './services/subdomain';
import {
  adoptAccountUnitSystem,
  setUnitSystem,
  useUnitSystem,
  type UnitSystem,
} from './services/useUnitSystem';
import {
  MAP_STYLE_DESCRIPTIONS,
  MAP_STYLE_LABELS,
  setMapStyle,
  useMapStyle,
  type MapStyle,
} from './services/useMapStyle';

// Wave 20.23 — Live broadcast is a paid feature. Any plan other
// than "free" grants it; Rapha members are considered paid by
// their membership.
function isPaidPlanId(planId: string | undefined | null): boolean {
  if (!planId) return false;
  return planId !== 'free';
}

// Rapha is a store-embedded aero-fit experience; the social "drops"
// layer (public identity for share links, inbox for received drops) is
// only for the commercial product on aero.chat.
const IS_RAPHA = isRaphaDomain();

interface User {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
  avatarUpdatedAt?: string | null;
  verifiedEmail: boolean;
  isActive: boolean;
  settings?: {
    sendMarketingEmails: boolean;
    hasCompletedOnboarding?: boolean;
    liveShareStream?: boolean;
    liveShareLocation?: boolean;
    notifyOnNewFollower?: boolean;
    notifyOnDropReceived?: boolean;
  };
}


// Wave 20.70 — patch a single settings key. The server merges the
// settings blob rather than replacing it, so sending one key is safe
// and avoids reading-modifying-writing the whole object.
async function putSettings(partial: Record<string, unknown>): Promise<void> {
  const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
  if (!token) return;
  const res = await fetch(`${process.env.REACT_APP_API}/users/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ settings: partial }),
  });
  if (!res.ok) throw new Error(`settings save failed (${res.status})`);
}

export default function Account(_props: { theme?: any }) {
  const { t } = useTranslation('account');
  const [user, setUser] = useState<User | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Wave 20.6 — public identity (username + displayName). Saved via
  // PATCH /users/me/profile independently of the personal-info form.
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  // Wave 20.49de — Avatar upload.
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [usernameSuccess, setUsernameSuccess] = useState<string | null>(null);
  const [sendMarketing, setSendMarketing] = useState(false);
  // Wave 18 — walkthrough replay control. UI switch is "Show walkthrough
  // on my next live session"; flipping it ON persists
  // hasCompletedOnboarding=false so the tour runs again. State is kept
  // as `replayOnboarding` so the switch reads naturally.
  const [replayOnboarding, setReplayOnboarding] = useState(false);
  const units = useUnitSystem();
  const mapStyle = useMapStyle();
  // Wave 20.21 — live-session sharing preferences. Peaks are
  // always captured; these govern the *live* feed experience.
  // Both default OFF — you have to opt in to broadcast your
  // stream + location to followers.
  const [liveShareStream, setLiveShareStream] = useState(false);
  // Wave 20.23 — location broadcast retired. Keep the state
  // variable so save-payload shape stays stable but always send
  // false; UI toggle removed.
  const [liveShareLocation] = useState(false);
  // Wave 20.39 — per-event email notification prefs. Absent =
  // enabled; user can flip these off in Preferences.
  const [notifyOnNewFollower, setNotifyOnNewFollower] = useState(true);
  const [notifyOnDropReceived, setNotifyOnDropReceived] = useState(true);
  const { subscription } = useSubscription();
  const isPaid = isPaidPlanId(subscription?.planId) || IS_RAPHA;
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Wave 20.44 — permanent account deletion (App Store 5.1.1(v)).
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
      const res = await fetch(`${process.env.REACT_APP_API}/users`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) {
        let msg = `Delete failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.message) msg = String(body.message);
        } catch { /* body wasn't JSON */ }
        setDeleteError(msg);
        setDeleting(false);
        return;
      }
      localStorage.removeItem(process.env.REACT_APP_TOKEN_KEY || '');
      window.location.href = '/login';
    } catch (e: any) {
      setDeleteError(e?.message || 'Network error');
      setDeleting(false);
    }
  };
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    fetch(`${process.env.REACT_APP_API}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((u: User) => {
        setUser(u);
        // Wave 20.70 — the account is the source of truth for metric
        // vs statute. It used to live only in this browser, so it
        // never followed the user to the phone and a cleared cache
        // reset it to metric.
        adoptAccountUnitSystem((u.settings as any)?.units, (next) =>
          putSettings({ units: next }),
        );
        setFirstName(u.firstName || '');
        setLastName(u.lastName || '');
        setUsername((u as any).username || '');
        setDisplayName((u as any).displayName || '');
        setSendMarketing(u.settings?.sendMarketingEmails ?? false);
        // Switch reads as "replay next session". If they've already
        // completed the tour, the switch starts off; toggling on tells
        // the API to re-arm hasCompletedOnboarding = false on save.
        setReplayOnboarding(u.settings?.hasCompletedOnboarding !== true);
        setLiveShareStream(!!u.settings?.liveShareStream);
        // Wave 20.23 — location broadcast retired; ignore any
        // stored value and always send false on save.
        setNotifyOnNewFollower(u.settings?.notifyOnNewFollower ?? true);
        setNotifyOnDropReceived(u.settings?.notifyOnDropReceived ?? true);
        setLoading(false);
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }, []);

  const handleSave = () => {
    if (!firstName || !lastName) {
      setErrorMsg(t('errorBothNames'));
      return;
    }

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    fetch(`${process.env.REACT_APP_API}/users/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '')}`,
      },
      body: JSON.stringify({
        firstName,
        lastName,
        settings: {
          units,
          sendMarketingEmails: sendMarketing,
          hasCompletedOnboarding: !replayOnboarding,
          liveShareStream,
          liveShareLocation,
          notifyOnNewFollower,
          notifyOnDropReceived,
        },
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.statusCode && data.statusCode > 200)
          throw new Error(data.message || 'Update failed');
        setUser(data);
        setEditing(false);
        setSaving(false);
        setSuccessMsg(t('profileUpdated'));
      })
      .catch((e) => {
        setErrorMsg(e.message || t('errorGeneric'));
        setSaving(false);
      });
  };

  const handleVerifyEmail = () => {
    if (!user) return;
    fetch(`${process.env.REACT_APP_API}/users/verify/email/${user.email}`)
      .then((r) => r.json())
      .then((data) => setSuccessMsg(data.message || t('verificationSent')))
      .catch((e) => setErrorMsg(e.message));
  };

  if (loading) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      </PageLayout>
    );
  }

  // Wave 20.49cu — Users created before Wave 20.x may not have
  // firstName/lastName populated (nullable in DB, older signup
  // flows only asked for username). Reach for the first character
  // whatever's available: name → username → 'A'.
  const initials = (() => {
    if (!user) return 'A';
    const a = user.firstName?.charAt(0) ?? '';
    const b = user.lastName?.charAt(0) ?? '';
    const both = `${a}${b}`.toUpperCase();
    if (both) return both;
    const uname = (user as any).username as string | undefined;
    if (uname && uname.length > 0) return uname.charAt(0).toUpperCase();
    return 'A';
  })();

  return (
    <PageLayout maxWidth="md">
      <Box sx={{ mb: 5 }}>
        <Typography variant="overline" sx={{ color: colors.brand }}>
          {t('overline')}
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.75rem' },
            fontWeight: 800,
            letterSpacing: '-0.02em',
            mt: 1,
          }}
        >
          {t('title')}
        </Typography>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMsg}
        </Alert>
      )}

      {/* Profile header card */}
      <Box
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          {/* Wave 20.49de — Uploadable avatar. Click to pick an
              image; server stores bytes + serves at
              /users/:id/avatar?v=updatedAt. Falls back to
              initials when no avatar is set. */}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={
                user?.avatarUpdatedAt
                  ? `${process.env.REACT_APP_API}/users/${user.id}/avatar?v=${new Date(user.avatarUpdatedAt).getTime()}`
                  : undefined
              }
              sx={{
                width: 80,
                height: 80,
                fontSize: '1.75rem',
                fontWeight: 800,
                bgcolor: 'rgba(106, 255, 69, 0.08)',
                color: colors.brand,
                border: `2px solid ${colors.borderFocus}`,
                cursor: 'pointer',
              }}
              onClick={() => avatarInputRef.current?.click()}
            >
              {!user?.avatarUpdatedAt && initials}
            </Avatar>
            <Box
              onClick={() => avatarInputRef.current?.click()}
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: colors.brand,
                color: colors.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: `2px solid ${colors.bg}`,
                fontSize: '0.9rem',
                fontWeight: 800,
              }}
              aria-label="Change avatar"
              title="Change avatar"
            >
              +
            </Box>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setAvatarUploading(true);
                try {
                  const jwt = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
                  const fd = new FormData();
                  fd.append('file', f);
                  const r = await fetch(
                    `${process.env.REACT_APP_API}/users/me/avatar`,
                    {
                      method: 'POST',
                      headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
                      body: fd,
                    },
                  );
                  if (!r.ok) throw new Error(String(r.status));
                  const j = await r.json();
                  setUser((prev) => (prev ? { ...prev, avatarUpdatedAt: j.updatedAt } : prev));
                  setSuccessMsg('Avatar updated.');
                } catch {
                  setErrorMsg('Couldn\'t upload that image. Try again.');
                } finally {
                  setAvatarUploading(false);
                  if (avatarInputRef.current) avatarInputRef.current.value = '';
                }
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              {user?.email}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              {user?.verifiedEmail ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 999,
                    backgroundColor: 'rgba(106, 255, 69, 0.08)',
                    border: `1px solid ${colors.borderFocus}`,
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 14, color: colors.brand }} />
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: colors.brand,
                    }}
                  >
                    {t('verified')}
                  </Typography>
                </Box>
              ) : (
                <Box
                  onClick={handleVerifyEmail}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 999,
                    backgroundColor: 'rgba(255, 181, 71, 0.1)',
                    border: `1px solid rgba(255, 181, 71, 0.3)`,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255, 181, 71, 0.15)' },
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 14, color: colors.warning }} />
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: colors.warning,
                    }}
                  >
                    {t('verifyEmail')}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Profile fields */}
      <Box
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('personalInfo')}
          </Typography>
          {!editing && (
            <Button variant="outlined" onClick={() => setEditing(true)}>
              {t('edit')}
            </Button>
          )}
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('firstName')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={!editing}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('lastName')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={!editing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('email')}
              value={user?.email || ''}
              disabled
              helperText={t('emailHelp')}
            />
          </Grid>
        </Grid>

        {editing && (
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ px: 3 }}
            >
              {saving ? (
                <CircularProgress size={20} sx={{ color: '#0A0E1A' }} />
              ) : (
                t('saveChanges')
              )}
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setEditing(false);
                setFirstName(user?.firstName || '');
                setLastName(user?.lastName || '');
              }}
            >
              {t('cancel')}
            </Button>
          </Stack>
        )}
      </Box>

      {/* Wave 20.6 — Public identity: username + display name.
          Hidden on rapha.aero.chat — drops are a commercial-only
          feature, so the public-facing identity has no surface there. */}
      {!IS_RAPHA && (
      <Box
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 2,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Public identity
        </Typography>
        <Typography sx={{ color: colors.textMuted, mb: 3, fontSize: '0.9rem' }}>
          What people see when you Drop a link and how you log in.
        </Typography>

        {usernameError && (
          <Alert severity="error" sx={{ mb: 2 }}>{usernameError}</Alert>
        )}
        {usernameSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>{usernameSuccess}</Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              helperText="3–30 chars, lowercase letters, digits, _ or -. You can log in with this."
              inputProps={{ maxLength: 30 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              helperText="Free-form, shown on public Drop Link pages."
              inputProps={{ maxLength: 80 }}
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            disabled={usernameSaving}
            onClick={async () => {
              setUsernameError(null);
              setUsernameSuccess(null);
              setUsernameSaving(true);
              try {
                const token = localStorage.getItem(
                  process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
                );
                const res = await fetch(`${process.env.REACT_APP_API}/users/me/profile`, {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ username, displayName }),
                });
                if (!res.ok) {
                  const body = await res.json().catch(() => ({}));
                  throw new Error(body?.message || `HTTP ${res.status}`);
                }
                const updated = await res.json();
                setUser(updated);
                setUsernameSuccess('Saved');
              } catch (e: any) {
                setUsernameError(e?.message || 'Save failed');
              } finally {
                setUsernameSaving(false);
              }
            }}
            sx={{ px: 3 }}
          >
            {usernameSaving ? <CircularProgress size={20} /> : 'Save identity'}
          </Button>
        </Stack>
      </Box>
      )}

      {/* Wave 20.23 — Drops inbox moved to the Feed page's "Sent
          to you" section so all incoming drops surface where the
          user actually looks for updates. */}

      {/* Preferences */}
      <Box
        sx={{
          p: 4,
          mb: 3,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          {t('preferences')}
        </Typography>

        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>{t('productUpdates')}</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {t('productUpdatesDesc')}
              </Typography>
            </Box>
            <Switch
              checked={sendMarketing}
              onChange={(e) => {
                setSendMarketing(e.target.checked);
                setEditing(true);
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Show walkthrough</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Replay the intro tour on your next live session.
              </Typography>
            </Box>
            <Switch
              checked={replayOnboarding}
              onChange={(e) => {
                setReplayOnboarding(e.target.checked);
                setEditing(true);
              }}
            />
          </Box>
          {/* Wave 20.39 — email notification prefs. Absent = on. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Email me when someone follows me</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                A one-line email pointing at the new follower's profile.
              </Typography>
            </Box>
            <Switch
              checked={notifyOnNewFollower}
              onChange={(e) => {
                setNotifyOnNewFollower(e.target.checked);
                setEditing(true);
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Email me when someone drops to me</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Direct drops only — activities or peaks a rider sends to you personally. Doesn't fire for public shares.
              </Typography>
            </Box>
            <Switch
              checked={notifyOnDropReceived}
              onChange={(e) => {
                setNotifyOnDropReceived(e.target.checked);
                setEditing(true);
              }}
            />
          </Box>
          {/* Wave 20.26 — Metric / statute toggle. Persists to
              localStorage synchronously so formatters can read
              during render; also saved to user.settings on Save so
              a fresh device picks up the preference. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Units</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {units === 'metric'
                  ? 'Metric — kilometres, km/h, °C.'
                  : 'Statute — miles, mph, °F.'}
              </Typography>
            </Box>
            <Switch
              checked={units === 'imperial'}
              onChange={(e) => {
                const next: UnitSystem = e.target.checked ? 'imperial' : 'metric';
                // Persist to the account straight away. Requiring a
                // separate Save press is how a toggle appears to
                // work and then reverts on the next device.
                setUnitSystem(next, (u) => putSettings({ units: u }));
                setEditing(true);
              }}
            />
          </Box>
          {/* Wave 20.49 — Privacy zones. Full editor lives on
              /privacy-zones — it needs a map picker + list of
              cards, too much surface for a single settings row. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Privacy zones</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Hide parts of your route (home, work) from anyone else who
                sees your activities.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => (window.location.href = '/privacy-zones')}
              sx={{ color: colors.text, borderColor: colors.border }}
            >
              Manage
            </Button>
          </Box>
          {/* Wave 20.49 — Map style picker. Applies to every
              RouteMap render (session detail, feed cards, shared
              activity, drop links, mobile). Default is Outdoors —
              bright topo with visible trails; rider called out
              the previous dark-only default made terrain hard to
              read. */}
          <Box>
            <Typography sx={{ fontWeight: 700, mt: 1, color: colors.text }}>
              Map style
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5, mb: 1.5 }}>
              {MAP_STYLE_DESCRIPTIONS[mapStyle]}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(Object.keys(MAP_STYLE_LABELS) as MapStyle[]).map((k) => {
                const active = k === mapStyle;
                return (
                  <Button
                    key={k}
                    onClick={() => setMapStyle(k)}
                    variant={active ? 'contained' : 'outlined'}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      backgroundColor: active ? colors.brand : 'transparent',
                      color: active ? '#0A0E1A' : colors.text,
                      borderColor: active ? colors.brand : colors.border,
                      '&:hover': {
                        backgroundColor: active ? colors.brand : 'rgba(106,255,69,0.08)',
                        borderColor: colors.brand,
                      },
                    }}
                  >
                    {MAP_STYLE_LABELS[k]}
                  </Button>
                );
              })}
            </Stack>
          </Box>
          {/* Wave 20.49k — Baseline framing. Reset when the rider
              changes bike / trainer / camera so their next aero
              session captures a fresh framing target. */}
          <BaselineFramingRow />
          {/* Wave 20.21 / 20.23 — Live-session sharing. Peaks
              captured during a ride are always saved to your
              session and can be dropped after the fact for free.
              Live broadcast to followers requires a paid plan. */}
          <Box>
            <Typography sx={{ fontWeight: 700, mt: 1, color: colors.text }}>
              Live sharing
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
              Peaks are always captured and stay under your control. Broadcasting
              live telemetry to followers <b>while a session is in progress</b>
              requires a paid plan.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                Live stream to followers
                {!isPaid && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 1,
                      backgroundColor: `${colors.brand}22`,
                      color: colors.brand,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      letterSpacing: '0.1em',
                      verticalAlign: 'middle',
                    }}
                  >
                    PAID
                  </Box>
                )}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Second-by-second power, speed, HR, cadence, and elapsed time —
                visible to your followers while you're recording.
              </Typography>
            </Box>
            <Switch
              checked={liveShareStream && isPaid}
              disabled={!isPaid}
              onChange={(e) => {
                setLiveShareStream(e.target.checked);
                setEditing(true);
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Cookie preferences</Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Change which categories of cookies aero.chat may use on this device.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => window.dispatchEvent(new Event('aero:cookies:reopen'))}
              sx={{ color: colors.text, borderColor: colors.border }}
            >
              Change
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Danger zone */}
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {t('sessionSection')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Stack spacing={2}>
          <Button
            variant="outlined"
            onClick={() => {
              localStorage.removeItem(process.env.REACT_APP_TOKEN_KEY || '');
              window.location.href = '/login';
            }}
            sx={{
              borderColor: colors.border,
              color: colors.textMuted,
              alignSelf: 'flex-start',
              '&:hover': {
                borderColor: colors.danger,
                color: colors.danger,
                backgroundColor: 'rgba(255, 71, 87, 0.05)',
              },
            }}
          >
            {t('signOut')}
          </Button>
          <Divider />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: colors.text }}>
              Delete account
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted, mb: 2 }}>
              Permanently delete your aero.chat account, every recorded session,
              every drop link you've sent, and every position you've dialed in.
              This can't be undone. Any paid subscription must be cancelled
              separately (App Store Subscriptions or the billing portal above).
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setDeleteConfirm('');
                setDeleteError(null);
                setDeleteOpen(true);
              }}
              sx={{
                borderColor: colors.danger,
                color: colors.danger,
                alignSelf: 'flex-start',
                '&:hover': {
                  borderColor: colors.danger,
                  backgroundColor: 'rgba(255, 71, 87, 0.08)',
                },
              }}
            >
              Delete my account
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Wave 20.44 — permanent account deletion confirm. Requires
          the rider to type DELETE so a click-through can't wipe
          their history. */}
      <Dialog
        open={deleteOpen}
        onClose={() => (deleting ? undefined : setDeleteOpen(false))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.bg,
            color: colors.text,
            border: `1px solid ${colors.danger}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete your account?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textMuted, mb: 2 }}>
            Type <Box component="span" sx={{ color: colors.danger, fontWeight: 800 }}>DELETE</Box> to confirm. This can't be undone.
          </Typography>
          <TextField
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder="DELETE"
            fullWidth
            autoFocus
            disabled={deleting}
            inputProps={{ autoCapitalize: 'characters' }}
            sx={{
              '& .MuiInputBase-input': { color: colors.text },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.danger },
            }}
          />
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, backgroundColor: 'rgba(255,71,87,0.08)' }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            sx={{ color: colors.textMuted }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={deleteConfirm !== 'DELETE' || deleting}
            sx={{ color: colors.danger, fontWeight: 800 }}
          >
            {deleting ? 'Deleting…' : 'Delete forever'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

// Wave 20.49k — Rider-facing reset for the baseline framing target
// Preflight uses to keep aero-session baselines repeatable. When
// the rider changes bike / trainer / camera, the old target is
// stale; hitting Reset here drops it and the next aero session
// captures a fresh one.
function BaselineFramingRow() {
  const [target, setTarget] = React.useState<{
    capturedAt: string;
  } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const token = React.useMemo(() => {
    try {
      return window.localStorage.getItem(
        process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
      );
    } catch {
      return null;
    }
  }, []);
  const api = process.env.REACT_APP_API || 'https://api.aero.chat';

  const load = React.useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${api}/users/me/calibration-target`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setTarget(body ?? null);
    } catch {
      setTarget(null);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const reset = async () => {
    if (!token) return;
    setResetting(true);
    setError(null);
    try {
      const res = await fetch(`${api}/users/me/calibration-target`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTarget(null);
    } catch {
      setError('Could not reset baseline framing.');
    } finally {
      setResetting(false);
    }
  };

  const capturedAtLabel = target?.capturedAt
    ? new Date(target.capturedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography sx={{ fontWeight: 600 }}>Baseline framing</Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted }}>
          {loading
            ? 'Loading…'
            : capturedAtLabel
            ? `Captured ${capturedAtLabel}. Reset if you've changed bike, trainer, or camera position.`
            : "You'll capture your baseline framing on your next aero session."}
        </Typography>
        {error && (
          <Typography variant="body2" sx={{ color: colors.danger, mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
      {target && (
        <Button
          variant="outlined"
          onClick={reset}
          disabled={resetting}
          sx={{ color: colors.text, borderColor: colors.border }}
        >
          {resetting ? 'Resetting…' : 'Reset'}
        </Button>
      )}
    </Box>
  );
}
